import { array, bool, boolean, InferType, lazy, number, object, ref, string } from "yup";
import { TUserRole, UserRole } from "../transports/user";
import { TWritingType } from "./orders/order";
import { PBNSchema } from "./PBN";
import { TOrderType } from "./orders/order";
import { TLangType } from "./LangType";

export type UserModel = InferType<typeof UserSchema>;

export const UserSchema: any = object({
    id: number().required(),
    email: string().required("error.user.email.required"),
    active: boolean().required("error.user.active.required"),
    firstName: string().required("error.user.firstName.required"),
    lastName: string().required("error.user.lastName.required"),
    lang: string<TLangType>().default("FR"),
    role: string<TUserRole>().required("error.user.role.required"),
    phoneNumber: string().required("error.user.phoneNumber.required"),
    orderType: string<TOrderType>().nullable(),
    writingType: string<TWritingType>().when("role", {
        is: (role: string) =>
            [UserRole.ROLE_WRITER, UserRole.ROLE_LEAD_WRITER]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.required("error.user.writingType.required"),
        otherwise: (schema) => schema.nullable(),
    }),
    pbns: array().of(PBNSchema).default(null).nullable(),
    quota: number().when("role", {
        is: (role: string) =>
            [
                UserRole.ROLE_WRITER,
                UserRole.ROLE_LEAD_WRITER,
                UserRole.ROLE_PROOFREADER,
                UserRole.ROLE_LEAD_PROOFREADER,
            ]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.integer().required("error.user.quota.required"),
    }),
    lead: lazy(() => UserSchema.default(undefined).nullable()),
    hasDuplicateContentCheck: bool()
        .default(true)
        .when("role", {
            is: (role: string) =>
                [UserRole.ROLE_WRITER, UserRole.ROLE_LEAD_WRITER]
                    .map((v) => v.toString())
                    .includes(role),
            then: (schema) => schema.required("error.user.hasDuplicateContentCheck.required"),
        }),
    priceWrite: number().when("role", {
        is: (role: string) =>
            [UserRole.ROLE_PROOFREADER, UserRole.ROLE_LEAD_PROOFREADER]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.required("error.user.priceLandingPage.required"),
    }),
    priceAiAuto: number().when("role", {
        is: (role: string) =>
            [UserRole.ROLE_PROOFREADER, UserRole.ROLE_LEAD_PROOFREADER]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.required("error.user.priceLandingPage.required"),
    }),
    priceAiManual: number().when("role", {
        is: (role: string) =>
            [UserRole.ROLE_PROOFREADER, UserRole.ROLE_LEAD_PROOFREADER]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.required("error.user.priceLandingPage.required"),
    }),
    priceTranslate: number().when("role", {
        is: (role: string) =>
            [UserRole.ROLE_PROOFREADER, UserRole.ROLE_LEAD_PROOFREADER]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.required("error.user.priceLandingPage.required"),
    }),
    priceRewrite: number().when("role", {
        is: (role: string) =>
            [UserRole.ROLE_PROOFREADER, UserRole.ROLE_LEAD_PROOFREADER]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.required("error.user.priceLandingPage.required"),
    }),
    priceExpired: number().when("role", {
        is: (role: string) =>
            [UserRole.ROLE_PROOFREADER, UserRole.ROLE_LEAD_PROOFREADER]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.required("error.user.priceLandingPage.required"),
    }),
    priceHomePage: number().when("role", {
        is: (role: string) =>
            [UserRole.ROLE_WRITER, UserRole.ROLE_LEAD_WRITER]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.required("error.user.priceHomePage.required"),
    }),
    priceBlog: number().when("role", {
        is: (role: string) =>
            [UserRole.ROLE_WRITER, UserRole.ROLE_LEAD_WRITER]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.required("error.user.priceBlog.required"),
    }),
    priceProductPage: number().when("role", {
        is: (role: string) =>
            [UserRole.ROLE_WRITER, UserRole.ROLE_LEAD_WRITER]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.required("error.user.priceProductPage.required"),
    }),

    priceLandingPage: number().when("role", {
        is: (role: string) =>
            [UserRole.ROLE_WRITER, UserRole.ROLE_LEAD_WRITER]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.required("error.user.priceLandingPage.required"),
    }),
    priceOptimizedExtra: number().when("role", {
        is: (role: string) =>
            [UserRole.ROLE_WRITER, UserRole.ROLE_LEAD_WRITER]
                .map((v) => v.toString())
                .includes(role),
        then: (schema) => schema.required("error.user.priceOptimizedExtra.required"),
    }),
}).required();

export const UpsertUserDTOSchema = UserSchema.omit(["active", "id", "lead", "pbns", "lang"]).concat(
    object({
        id: number(),
        password: string().when("id", {
            is: (id: number | undefined) => id == undefined,
            then: (schema) => schema.required("error.user.passwordConfirmation.required"),
            otherwise: (schema) => schema.default(""),
        }),
        passwordConfirmation: string()
            .oneOf([ref("password")], "error.user.passwordConfirmation.required")
            .default(""),
        leadId: number().when("role", {
            is: (role: string) =>
                [
                    UserRole.ROLE_WRITER,
                    UserRole.ROLE_PROOFREADER,
                    UserRole.ROLE_BUSINESS_MANAGER,
                    UserRole.ROLE_LINKER,
                    UserRole.ROLE_PROJECT_MANAGER,
                ]
                    .map((v) => v.toString())
                    .includes(role),
            then: (schema) => schema.integer().required("error.user.leadId.required"),
        }),
        pbnIds: array().of(string()).default(null).nullable(),
        lang: string<TLangType>()
            .when("role", {
                is: (role: TUserRole) =>
                    role === UserRole.ROLE_LEAD_WRITER ||
                    role === UserRole.ROLE_WRITER ||
                    role === UserRole.ROLE_PROOFREADER ||
                    role === UserRole.ROLE_LEAD_PROOFREADER,
                then: (schema) => schema.required(),
            })
            .default("FR"),
    })
);

export type UpsertUserDTO = InferType<typeof UpsertUserDTOSchema>;

export type AuthModel = {
    token: string;
    refreshToken?: string;
};
